import { HttpRequest, HttpMethod, axiosInstance } from "./http";

// ModernTradeProvider — API calls for the "Modern Trade" module (epic mdf-bo#113+).
// All endpoints under /api/v3/modern-trade, JWT + mw.IsModernTrade gate.
class ModernTradeProvider extends HttpRequest {
    // ── Companies ──────────────────────────────────────────────────────────────
    companiesList(params) {
        return this.request(HttpMethod.Get, "/api/v3/modern-trade/companies", params);
    }
    companyCreate(payload) {
        return this.request(HttpMethod.Post, "/api/v3/modern-trade/companies", payload);
    }
    companyGet(id) {
        return this.request(HttpMethod.Get, `/api/v3/modern-trade/companies/${id}`);
    }
    companyUpdate(id, payload) {
        return this.request(HttpMethod.Put, `/api/v3/modern-trade/companies/${id}`, payload);
    }
    companyDelete(id) {
        return this.request(HttpMethod.Delete, `/api/v3/modern-trade/companies/${id}`);
    }

    // ── Goods Receipts ─────────────────────────────────────────────────────────
    // multipart FormData: company_id, po_number, note?, file (PO document)
    receiptCreate(formData) {
        return this.request(HttpMethod.Post, "/api/v3/modern-trade/receipts", formData);
    }
    receiptsList(params) {
        return this.request(HttpMethod.Get, "/api/v3/modern-trade/receipts", params);
    }
    receiptGet(id) {
        return this.request(HttpMethod.Get, `/api/v3/modern-trade/receipts/${id}`);
    }
    // Returns { url, expires_in, filename } presigned GET (5 min)
    receiptPoFile(id) {
        return this.request(HttpMethod.Get, `/api/v3/modern-trade/receipts/${id}/po-file`);
    }
    // multipart FormData: file (.xlsx/.xls)
    receiptImportValidate(id, formData) {
        return this.request(HttpMethod.Post, `/api/v3/modern-trade/receipts/${id}/import/validate`, formData);
    }
    // body: { batch_id }
    receiptImportConfirm(id, payload) {
        return this.request(HttpMethod.Post, `/api/v3/modern-trade/receipts/${id}/import/confirm`, payload);
    }
    receiptDelete(id) {
        return this.request(HttpMethod.Delete, `/api/v3/modern-trade/receipts/${id}`);
    }

    // ── Sales Imports ──────────────────────────────────────────────────────────
    // multipart FormData: file (.xlsx/.xls)
    salesImportValidate(formData) {
        return this.request(HttpMethod.Post, "/api/v3/modern-trade/sales/import/validate", formData);
    }
    // body: { batch_id } — 409 while row_error > 0 (all-or-nothing, LOCKED Q1)
    salesImportConfirm(payload) {
        return this.request(HttpMethod.Post, "/api/v3/modern-trade/sales/import/confirm", payload);
    }
    salesImportList(params) {
        return this.request(HttpMethod.Get, "/api/v3/modern-trade/sales/imports", params);
    }

    // ── Settings (LOCKED Q10) ──────────────────────────────────────────────────
    // → { min_shelf_life_days,
    //     sale_channel_id, sale_channel_name, sale_channel_usable,
    //     payment_method_id, payment_method_name, payment_method_usable }
    // *_usable = false when the row it points at is gone or soft-deleted; a value
    // that was never set comes back as id 0 / name "" / usable false. The screen
    // MUST tell those two apart — "pointed at a row that no longer exists" and
    // "never configured" need different actions from the operator.
    settingsGet() {
        return this.request(HttpMethod.Get, "/api/v3/modern-trade/settings");
    }
    // The option lists for the two dropdowns above (bo-api #512). It lives inside
    // this module on purpose: /sale-channels and /payment-methods of the other
    // modules are SuperAdmin-gated and answer an MT operator with 403
    // access_denied — a permission dialog from http.js on top of the page.
    // (Before bo-api #937 it was 401, which http.js reads as an expired token
    // and logged the user out mid-task — mdf-bo-fn#236.) Every path in this
    // file must stay under /api/v3/modern-trade/.
    // → { sale_channels: [{ id, name }], payment_methods: [{ id, name }] }
    settingsOptions() {
        return this.request(HttpMethod.Get, "/api/v3/modern-trade/settings/options");
    }
    // body: { min_shelf_life_days?, sale_channel_id?, payment_method_id? } — every
    // key optional, only what is sent is written.
    //
    // deduct_main_stock was RETIRED with the delivery-note stock cut (bo-api #505).
    // Do not send it: the endpoint answers 400 setting_retired and returns BEFORE
    // it looks at min_shelf_life_days, so one stray key makes the whole settings
    // page unsaveable. The same shape of trap applies to the two ids: a rejected
    // id (400 invalid_sale_channel / sale_channel_not_selectable /
    // invalid_payment_method) aborts the whole PUT, so callers must not resend an
    // id the server already refuses just because it is the value on screen.
    settingsPut(payload) {
        return this.request(HttpMethod.Put, "/api/v3/modern-trade/settings", payload);
    }

    // ── Import Batches (shared) ────────────────────────────────────────────────
    batchGet(batchId, params) {
        return this.request(HttpMethod.Get, `/api/v3/modern-trade/imports/${batchId}`, params);
    }
    batchCancel(batchId) {
        return this.request(HttpMethod.Post, `/api/v3/modern-trade/imports/${batchId}/cancel`, {});
    }
    // Returns blob (.xlsx row-status report)
    batchReportExport(batchId) {
        return axiosInstance.get(`/api/v3/modern-trade/imports/${batchId}/report`, { responseType: "blob" });
    }

    // ── Stock ──────────────────────────────────────────────────────────────────
    // q: page, per_page, company_id, search, group_by (lot|product), nonzero
    stockList(params) {
        return this.request(HttpMethod.Get, "/api/v3/modern-trade/stock", params);
    }
    // q: page, per_page, company_id, product_id, lot_no, movement_type, date_from, date_to
    stockMovements(params) {
        return this.request(HttpMethod.Get, "/api/v3/modern-trade/stock/movements", params);
    }
    // body: { company_id, product_id, lot_no, qty (signed ≠0), reason }
    stockAdjust(payload) {
        return this.request(HttpMethod.Post, "/api/v3/modern-trade/stock/adjust", payload);
    }

    // ── Reports ────────────────────────────────────────────────────────────────
    // q: company_id?, days_ahead, bucket?, page, per_page
    expiryReport(params) {
        return this.request(HttpMethod.Get, "/api/v3/modern-trade/reports/expiry", params);
    }
    // Returns blob (.xlsx)
    expiryReportExport(params) {
        return axiosInstance.get("/api/v3/modern-trade/reports/expiry/export", { params, responseType: "blob" });
    }

    // ── Near-expiry returns (spec 2026-11-03 §5) ──────────────────────────────
    // q: days (450|365|180), company_id? → Data = rows (r.Append each; Data
    // absent when none) {company_id, company_name, product_id (wms), barcode,
    // name, code, lot_no, expired_date, days_left, qty}
    expiryReturnCandidates(params) {
        return this.request(HttpMethod.Get, "/api/v3/modern-trade/expiry-returns/candidates", params);
    }
    // body: { company_id, product_id (wms), lot_no, qty, return_date, note }
    // → Data[0] = the created return row
    // 409 insufficient_stock + Datum {available} · 409 sale_order_closed (Cause =
    // Thai text naming the SO) · 400 lot_not_found / future_date / bad input
    expiryReturnCreate(payload) {
        return this.request(HttpMethod.Post, "/api/v3/modern-trade/expiry-returns", payload);
    }
    // q: from, to, company_id?, page, per_page
    // → Data[0] = { items: [rows + company_name, barcode, name, code, created_by_name], total, page, per_page }
    expiryReturnsList(params) {
        return this.request(HttpMethod.Get, "/api/v3/modern-trade/expiry-returns", params);
    }

    // ════════════════════════════════════════════════════════════════════════════
    // Modern Trade v2 — pricing, sales flows & documents (epic mdf-project#144+)
    // ════════════════════════════════════════════════════════════════════════════

    // ── Products (MT Master Data) ────────────────────────────────────────────────
    // q: page, per_page, search (code/name), status
    productsList(params) {
        return this.request(HttpMethod.Get, "/api/v3/modern-trade/products", params);
    }
    // Picker search (picker=1): matches name|barcode only and returns each row
    // with `barcode`. productsList without picker keeps the code|name search
    // that the product settings page relies on.
    productsPicker(params) {
        return this.productsList({ ...(params || {}), picker: 1 });
    }
    productCreate(payload) {
        return this.request(HttpMethod.Post, "/api/v3/modern-trade/products", payload);
    }
    productGet(id) {
        return this.request(HttpMethod.Get, `/api/v3/modern-trade/products/${id}`);
    }
    productUpdate(id, payload) {
        return this.request(HttpMethod.Put, `/api/v3/modern-trade/products/${id}`, payload);
    }
    productDelete(id) {
        return this.request(HttpMethod.Delete, `/api/v3/modern-trade/products/${id}`);
    }
    // multipart FormData: file (.xlsx/.xls)
    productImportValidate(formData) {
        return this.request(HttpMethod.Post, "/api/v3/modern-trade/products/import/validate", formData);
    }
    // body: { batch_id } — upsert by code
    productImportConfirm(payload) {
        return this.request(HttpMethod.Post, "/api/v3/modern-trade/products/import/confirm", payload);
    }
    // Returns blob (.xlsx Master Data template)
    productImportTemplate() {
        return axiosInstance.get("/api/v3/modern-trade/products/import/template", { responseType: "blob" });
    }
    // Lightweight wms product list for the sku dropdown: { items: [{id, name, man_pn}] } (SKU = man_pn)
    wmsProductOptions(params) {
        return this.request(HttpMethod.Get, `/api/v3/modern-trade/wms-product-options`, params);
    }
    // Picker search (picker=1): matches name|barcode only (not man_pn) and
    // returns { items: [{id, name, man_pn, barcode}] }.
    wmsProductPicker(params) {
        return this.wmsProductOptions({ ...(params || {}), picker: 1 });
    }
    // Lightweight wms partner list (บริษัทผู้ผลิต): { items: [{id, company_name}] }
    wmsPartnerOptions(params) {
        return this.request(HttpMethod.Get, `/api/v3/modern-trade/wms-partner-options`, params);
    }
    // ── Product categories (dropdown master) ────────────────────────────────────
    categoriesList() {
        return this.request(HttpMethod.Get, `/api/v3/modern-trade/categories`);
    }
    categoryCreate(payload) {
        return this.request(HttpMethod.Post, `/api/v3/modern-trade/categories`, payload);
    }
    categoryUpdate(id, payload) {
        return this.request(HttpMethod.Put, `/api/v3/modern-trade/categories/${id}`, payload);
    }
    categoryDelete(id) {
        return this.request(HttpMethod.Delete, `/api/v3/modern-trade/categories/${id}`);
    }

    // ── Price Templates ──────────────────────────────────────────────────────────
    priceTemplatesList(params) {
        return this.request(HttpMethod.Get, "/api/v3/modern-trade/price-templates", params);
    }
    priceTemplateCreate(payload) {
        return this.request(HttpMethod.Post, "/api/v3/modern-trade/price-templates", payload);
    }
    priceTemplateGet(id) {
        return this.request(HttpMethod.Get, `/api/v3/modern-trade/price-templates/${id}`);
    }
    priceTemplateUpdate(id, payload) {
        return this.request(HttpMethod.Put, `/api/v3/modern-trade/price-templates/${id}`, payload);
    }
    priceTemplateDelete(id) {
        return this.request(HttpMethod.Delete, `/api/v3/modern-trade/price-templates/${id}`);
    }
    priceTemplateItems(id) {
        return this.request(HttpMethod.Get, `/api/v3/modern-trade/price-templates/${id}/items`);
    }
    // body: { items: [{ product_id, retail_price }] } — batch replace set
    priceTemplateItemsUpdate(id, payload) {
        return this.request(HttpMethod.Put, `/api/v3/modern-trade/price-templates/${id}/items`, payload);
    }

    // ── Sale Orders ──────────────────────────────────────────────────────────────
    // multipart FormData: company_id, sale_type, shipping_type, po_number, po_date?, note?,
    //   bill_discount?, file (PO), items (JSON string [{product_id, qty, discount}])
    // { draft: true } appends draft=1: the BE then needs only company_id, sale_type and
    //   shipping_type (po_number / file may be empty) and the order stays status 1.
    saleOrderCreate(formData, { draft } = {}) {
        if (draft && formData instanceof FormData) formData.append("draft", "1");
        return this.request(HttpMethod.Post, "/api/v3/modern-trade/sale-orders", formData);
    }
    // q: page, per_page, company_id, sale_type, status, search (so_no/po_number)
    saleOrdersList(params) {
        return this.request(HttpMethod.Get, "/api/v3/modern-trade/sale-orders", params);
    }
    saleOrderGet(id) {
        return this.request(HttpMethod.Get, `/api/v3/modern-trade/sale-orders/${id}`);
    }
    // draft (status 1) only; 409 not_draft otherwise. multipart FormData: company_id,
    //   sale_type, shipping_type, po_number, po_date, note, bill_discount,
    //   items (JSON string [{product_id, qty, discount}]), file? (replaces the PO file).
    //   The BE re-prices every line. JSON bodies are still accepted (legacy).
    saleOrderUpdate(id, formData) {
        return this.request(HttpMethod.Put, `/api/v3/modern-trade/sale-orders/${id}`, formData);
    }
    // 400 with Msg = missing_po_number | missing_po_file | no_items when the draft is incomplete.
    saleOrderConfirm(id) {
        return this.request(HttpMethod.Post, `/api/v3/modern-trade/sale-orders/${id}/confirm`, {});
    }
    saleOrderCancel(id) {
        return this.request(HttpMethod.Post, `/api/v3/modern-trade/sale-orders/${id}/cancel`, {});
    }
    // #154 (BE mdf-bo-api#303): delivered (5) → closed (6); 400 when the SO is not
    // in the delivered state (BE owns the guard). 2026-11-04: goods still owed →
    // 409 outstanding_items + Data[0].outstanding unless payload is
    // { confirm_outstanding: true }.
    saleOrderClose(id, payload) {
        return this.request(HttpMethod.Post, `/api/v3/modern-trade/sale-orders/${id}/close`, payload || {});
    }
    // Returns { url, expires_in, filename } presigned GET
    saleOrderPoFile(id) {
        return this.request(HttpMethod.Get, `/api/v3/modern-trade/sale-orders/${id}/po-file`);
    }
    // Gen MO for every manufacturer (not yet issued) → returns MO list
    saleOrderProductionOrders(id) {
        return this.request(HttpMethod.Post, `/api/v3/modern-trade/sale-orders/${id}/production-orders`, {});
    }
    // multipart FormData: file (Lot/Expire filled by factory)
    saleOrderGoodsValidate(id, formData) {
        return this.request(HttpMethod.Post, `/api/v3/modern-trade/sale-orders/${id}/goods/validate`, formData);
    }
    // body: { batch_id }
    saleOrderGoodsConfirm(id, payload) {
        return this.request(HttpMethod.Post, `/api/v3/modern-trade/sale-orders/${id}/goods/confirm`, payload);
    }
    // Nov-5 §4: manual Lot/expiry entry (partial receipts allowed).
    // body: { lines: [{ product_id (bo_mt_product), lot_no, expired_date "YYYY-MM-DD", qty }] }
    // 200 Data[0] { receipt_id, fully_received } · 400 Msg = first code,
    // Datum { errors: [{ row_no (1-based line), code }] }
    saleOrderGoodsManual(id, payload) {
        return this.request(HttpMethod.Post, `/api/v3/modern-trade/sale-orders/${id}/goods/manual`, payload);
    }
    // Data = rows (r.Append each) [{ product_id, product_code, barcode, name, code,
    // manufacturer, ordered, received, remaining }]
    saleOrderGoodsProgress(id) {
        return this.request(HttpMethod.Get, `/api/v3/modern-trade/sale-orders/${id}/goods/progress`);
    }
    // Returns blob (.xlsx) — one Lot/Expire template per SO (rows with remaining > 0)
    saleOrderLotTemplate(id) {
        return axiosInstance.get(`/api/v3/modern-trade/sale-orders/${id}/lot-template`, { responseType: "blob" });
    }
    // บันทึกรับเงินที่ SO — round-19: เป็นประวัติการรับเงินล้วน (ยังไม่ออกใบกำกับ)
    // body: { amount, paid_date, method?, note?, slip_object_key? }. ออกใบกำกับผ่าน saleOrderIssueInvoice()
    saleOrderPaymentCreate(id, payload) {
        return this.request(HttpMethod.Post, `/api/v3/modern-trade/sale-orders/${id}/payments`, payload);
    }
    saleOrderDeliveries(id, payload) {
        return this.request(HttpMethod.Post, `/api/v3/modern-trade/sale-orders/${id}/deliveries`, payload);
    }

    // ── Production Orders / Deliveries (documents) ───────────────────────────────
    // Returns blob (PDF ใบสั่งผลิต)
    productionOrderPdf(id) {
        return axiosInstance.get(`/api/v3/modern-trade/production-orders/${id}/pdf`, { responseType: "blob" });
    }
    // Returns blob (.xlsx Lot/Expire template)
    productionOrderLotTemplate(id) {
        return axiosInstance.get(`/api/v3/modern-trade/production-orders/${id}/lot-template`, { responseType: "blob" });
    }
    // Returns blob (PDF ใบส่งของ)
    deliveryPdf(id) {
        return axiosInstance.get(`/api/v3/modern-trade/deliveries/${id}/pdf`, { responseType: "blob" });
    }

    // ── Invoices + Payments ──────────────────────────────────────────────────────
    // body: { sale_order_id } — ขายขาดเท่านั้น
    // RETIRED (spec 2026-11-03 §3A): BE ตอบ 409 use_delivery_invoice → saleOrderInvoiceFromDelivery
    // spec 2026-08-13 (money-driven): { sales_batch_id } เลิกใช้แล้ว BE ตอบ 409
    // use_consignment_invoices → ใบฝากขายออกที่ consignmentInvoiceCreate ด้านล่าง
    invoiceCreate(payload) {
        return this.request(HttpMethod.Post, "/api/v3/modern-trade/invoices", payload);
    }
    // q: page, per_page, company_id, status, date_from, date_to
    invoicesList(params) {
        return this.request(HttpMethod.Get, "/api/v3/modern-trade/invoices", params);
    }
    invoiceGet(id) {
        return this.request(HttpMethod.Get, `/api/v3/modern-trade/invoices/${id}`);
    }
    // Returns blob (PDF layout IV6)
    invoicePdf(id) {
        return axiosInstance.get(`/api/v3/modern-trade/invoices/${id}/pdf`, { responseType: "blob" });
    }
    invoiceCancel(id) {
        return this.request(HttpMethod.Post, `/api/v3/modern-trade/invoices/${id}/cancel`, {});
    }
    // body: { amount, paid_date, method, note? }
    invoicePayments(id, payload) {
        return this.request(HttpMethod.Post, `/api/v3/modern-trade/invoices/${id}/payments`, payload);
    }
    paymentDelete(id) {
        return this.request(HttpMethod.Delete, `/api/v3/modern-trade/payments/${id}`);
    }

    // ── Reports (v2) ─────────────────────────────────────────────────────────────
    // q: date_from, date_to, company_id?, group_by (company|product|invoice)
    reportSales(params) {
        return this.request(HttpMethod.Get, "/api/v3/modern-trade/reports/sales", params);
    }
    // Returns blob (.xlsx)
    reportSalesExport(params) {
        return axiosInstance.get("/api/v3/modern-trade/reports/sales/export", { params, responseType: "blob" });
    }
    // q: company_id?
    reportStockValue(params) {
        return this.request(HttpMethod.Get, "/api/v3/modern-trade/reports/stock-value", params);
    }
    // Returns blob (.xlsx)
    reportStockValueExport(params) {
        return axiosInstance.get("/api/v3/modern-trade/reports/stock-value/export", { params, responseType: "blob" });
    }

    // ════════════════════════════════════════════════════════════════════════════
    // Modern Trade — Round-3 (issues #163/#164/#166). API contract:
    // docs/specs/2026-07-08-round3-plan.md §2. Append-only; wired by D1/D2/D3.
    // ════════════════════════════════════════════════════════════════════════════

    // ── Sales Entries (#163 — "ข้อมูลการขาย": saved sales + manual entry) ─────────
    // q: page, per_page, company_id?, date_from?, date_to?, search?
    // → { data:[{id,doc_no,company_id,company_name,branch_code,sold_at,item_count,total_qty,source,created_at}], total }
    salesEntriesList(params) {
        return this.request(HttpMethod.Get, "/api/v3/modern-trade/sales-entries", params);
    }
    // → header + items:[{sku_code,barcode,product_code,wms_product_id,product_name,lot_no,qty,deducted:[{lot_no,qty}]}]
    salesEntryGet(id) {
        return this.request(HttpMethod.Get, `/api/v3/modern-trade/sales-entries/${id}`);
    }
    // body: { company_id, branch_code?("00000"), sold_at, items:[{sku_code|barcode|product_code, lot_no?, qty}] }
    // → { id, doc_no }. Deducts stock (lot-specific when lot_no set, else FEFO). 400 on validate fail = not saved.
    salesEntryCreate(payload) {
        return this.request(HttpMethod.Post, "/api/v3/modern-trade/sales-entries", payload);
    }
    // Lightweight lot picker for manual entry. q: wms_product_id
    // → { data:[{lot_no, expire_date, qty_remaining}] } (must NOT use WmsProductListV3 — LOCKED)
    lotOptions(params) {
        return this.request(HttpMethod.Get, "/api/v3/modern-trade/lot-options", params);
    }
    // NOTE (#163): salesImportValidate(formData) / salesImportConfirm(payload) already exist above.
    //   Round-3 change is caller-side only — append `company_id` (+ `branch_code`, default "00000")
    //   to the FormData / confirm body instead of parsing tax_id from the file. No new method needed.

    // ── Document format settings (#166 — production/delivery/invoice PDF config) ──
    // q: doc_type = production | delivery | invoice → { doc_type, config:{...} }
    docFormatGet(params) {
        return this.request(HttpMethod.Get, "/api/v3/modern-trade/doc-format", params);
    }
    // body: { doc_type, config:{ color, show_logo, show_stamp, fields:{key:{show,label}},
    //   columns:{...}, summary:{...}, footer_note, sign_left_label, sign_right_label } }
    docFormatSave(payload) {
        return this.request(HttpMethod.Put, "/api/v3/modern-trade/doc-format", payload);
    }

    // NOTE (#164): saleOrdersList(params) already accepts arbitrary params — Round-3 adds
    //   `created_from` / `created_to` filters (caller passes them). saleOrderCreate(formData)
    //   accepts a new `po_date` field (caller appends). No new methods needed.

    // ── Print preview (#183 — full-screen print-set preview) ─────────────────────
    // → { Data: [ {type,id,doc_no}, ... ] } type ∈ so|invoice|delivery|production
    printOptions(soId) {
        return this.request(HttpMethod.Get, `/api/v3/modern-trade/sale-orders/${soId}/print-options`);
    }
    // docsSpec = comma-separated "type:copytype:qty" units. Returns a single PDF blob (inline).
    printSet(soId, docsSpec) {
        return axiosInstance.get(`/api/v3/modern-trade/sale-orders/${soId}/print-set`, {
            params: { docs: docsSpec },
            responseType: "blob",
        });
    }

    // ════════════════════════════════════════════════════════════════════════════
    // round-19 Dev-A — invoice-from-payments (§2) + slip attach (§5).
    // Append-only. Payments are now SO history (invoice_id NULL); issuing an
    // invoice is an explicit action consuming the un-invoiced payment pool.
    // ════════════════════════════════════════════════════════════════════════════

    // Issue a tax invoice from the SO's un-invoiced payment pool. Returns
    // { invoice, items }. Errors: 400 no_unpaid_payment / no_delivery, 409 sale_order_closed.
    saleOrderIssueInvoice(id) {
        return this.request(HttpMethod.Post, `/api/v3/modern-trade/sale-orders/${id}/invoices`, {});
    }
    // Presign a payment-slip upload (object_type forced "mt_payment" server-side).
    // Body: { file_name, content_type }. Returns { upload_url, cdn_url, object_key, expires_in }.
    slipPresign(payload) {
        return this.request(HttpMethod.Post, "/api/v3/modern-trade/uploads/slip-presign", payload);
    }
    // Cancel an invoice (any source). round-19: allowed even with payments —
    // payments unlink back to the pool. body: { reason? }.
    invoiceCancelR19(id, payload) {
        return this.request(HttpMethod.Post, `/api/v3/modern-trade/invoices/${id}/cancel`, payload || {});
    }
    // round-19 Dev-B — Audit log + delivery ship-status
    // params: page, per_page, entity_type?, action?, actor_id?, doc_no?, date_from?, date_to?
    // → { Data:[{ total, page, per_page, items:[MtAuditLogRow] }] }
    auditLogs(params) {
        return this.request(HttpMethod.Get, "/api/v3/modern-trade/audit-logs", params);
    }
    // body: { ship_status: 1|2, tracking_number? } — tracking is accepted for every
    // shipping type (the DO is the only place a hand-shipped parcel's number lives).
    // 409 has_bo_order when the DO owns a BO order that is not cancelled: that
    // parcel ships through the order, so its number belongs to the order.
    deliveryShipStatus(id, payload) {
        return this.request(HttpMethod.Put, `/api/v3/modern-trade/deliveries/${id}/ship-status`, payload);
    }
    // 2026-11-04: cancel an UNUSED delivery note. body { reason? }. 409 codes:
    // delivery_already_cancelled / delivery_already_shipped / delivery_already_invoiced / delivery_has_bo_order /
    // delivery_stock_cut / delivery_stock_consumed / sale_order_closed /
    // sale_order_cancelled — Cause is a Thai sentence naming the blocker.
    // → Data[0] { delivery_id, doc_no, status: 9, sale_order_id, sale_order_status }
    deliveryCancel(id, payload) {
        return this.request(HttpMethod.Post, `/api/v3/modern-trade/deliveries/${id}/cancel`, payload || {});
    }

    // ── สร้างออเดอร์เข้าห้องแพ็คจากใบส่งของ (bo-api #504 / #505) ──────────────────
    // Issuing a DO moves no stock at all any more. Stock is reserved only when an
    // operator presses this button, so the preview below is not decoration: the
    // quantities it returns are the quantities that will be held against the stock
    // published to Lazada / Shopee / TikTok on the next sync.
    //
    // → { can_create, reason_code, reason_message,
    //     recipient:{ company_name, address, phone },
    //     shipping:{ type, provider_locked, provider_id, provider_name },
    //     lines:[{ wms_product_id, name, barcode, quantity, mt_product_codes[] }],
    //     existing_bo_order:{ id, order_status, status_text, tracking_number, stock_was_cut } }
    // reason_code ∈ ok | already_created | stock_already_cut | shipped_manually |
    //               missing_wms_mapping | missing_lot_expire | not_applicable |
    //               setting_missing | legacy_bo_order
    deliveryBoOrderPreview(id) {
        return this.request(HttpMethod.Get, `/api/v3/modern-trade/deliveries/${id}/bo-order/preview`);
    }
    // body: { shipping_provider_id } (omit when shipping.provider_locked)
    // → 201 { bo_order_id, order_status, tracking_number } · tracking_number may be
    //   "" when the carrier issues numbers asynchronously.
    // → 409 / 400 with reason_code + reason_message.
    deliveryBoOrderCreate(id, payload) {
        return this.request(HttpMethod.Post, `/api/v3/modern-trade/deliveries/${id}/bo-order`, payload || {});
    }

    // ════════════════════════════════════════════════════════════════════════════
    // round-19 Dev-C — document search (#363 §1) + price preview (#363 §6)
    // ════════════════════════════════════════════════════════════════════════════

    // ── Document search ──────────────────────────────────────────────────────────
    // q: q (doc number, >= 3 chars). Searches every MT doc type and returns
    // { items: [{ type, id, doc_no, sale_order_id?, receipt_id?, company_name?, status? }] }
    // type ∈ sale_order | po | production_order | delivery | invoice | receipt.
    documentSearch(q) {
        return this.request(HttpMethod.Get, "/api/v3/modern-trade/documents/search", { q });
    }

    // ── Price preview ────────────────────────────────────────────────────────────
    // q: company_id, product_id, qty?, discount?
    // → { retail_price, gp_percent, vat_included, unit_price, amount, source, found }
    // source ∈ "template" | "default"; found=false → no price for this product.
    pricePreview(params) {
        return this.request(HttpMethod.Get, "/api/v3/modern-trade/sale-orders/price-preview", params);
    }

    // ════════════════════════════════════════════════════════════════════════════
    // ฝากขาย — รับเงินผูกกับบริษัทคู่ค้า (spec 2026-08-13 รอบบ่าย: money-driven)
    // คู่ค้าโอนเงินมาก่อนตามยอดขายจริง → บันทึกรับเงินเข้า "กอง" ของบริษัท
    // (sale_order_id NULL + invoice_id NULL) → ผู้ใช้ "เลือก" รายการรับเงินที่จะออกใบ
    // → ใบกำกับออกตามยอดเงินที่เลือก รายการสินค้าดึงจากยอดขายจริงที่ยังไม่ออกใบ (เก่าก่อน)
    // ════════════════════════════════════════════════════════════════════════════

    // ── Consignment payments ─────────────────────────────────────────────────────
    // q: company_id?, date_from?, date_to?, link_status? (unlinked|linked|""), page, per_page
    // → { total, page, per_page, items:[{ id, company_id, company_name, amount, paid_date,
    //     method, note, slip_s3_key, invoice_id, invoice_doc_no, created_at, created_by_name }] }
    // เฉพาะเงินฝากขาย (sale_order_id IS NULL) — เงินใต้ SO ขายขาดไม่โผล่เส้นนี้
    consignmentPaymentsList(params) {
        return this.request(HttpMethod.Get, "/api/v3/modern-trade/consignment-payments", params);
    }
    // q: company_id? (ไม่ส่ง = ทุกบริษัทที่มีความเคลื่อนไหว)
    // → ต่อบริษัท: { company_id, company_name, total_received, unlinked_amount (กองรอออกใบ),
    //   last_paid_date, unbilled_sales_amount, issuable_amount_incl_vat }
    // unbilled_sales_amount = ยอดขายที่ยังออกใบได้ "ฐานภาษี" (ไม่รวม VAT)
    // issuable_amount_incl_vat = ตัวเดียวกันรวม VAT แล้ว → ตัวที่เอาไปเทียบกับเงินที่รับ
    //   (และเป็นตัวเดียวกับ max_amount ของ insufficient_sales) ทั้งคู่ไม่ใช่เพดานการรับเงิน
    consignmentPaymentsSummary(params) {
        return this.request(HttpMethod.Get, "/api/v3/modern-trade/consignment-payments/summary", params);
    }
    // body: { company_id, amount, paid_date, method?, note?, slip_object_key? }
    // ไม่มีเพดาน — คู่ค้าโอนมาเท่าไรบันทึกได้เท่านั้น (LOCKED)
    consignmentPaymentCreate(payload) {
        return this.request(HttpMethod.Post, "/api/v3/modern-trade/consignment-payments", payload);
    }
    // body: { invoice_id, amount? } ผูก / { invoice_id: null } ปลด — ใช้ได้เฉพาะเงินฝากขาย
    // (payment ของ SO ขายขาด → 409 wrong_payment_type). ผูกเกิน net_amount → 400 payment_exceeds
    // spec 2026-11-03 §3B: amount < ยอดเงิน → BE แตกแถว ส่วนที่เหลือกลับเข้ากองรอผูก
    paymentLink(id, payload) {
        return this.request(HttpMethod.Put, `/api/v3/modern-trade/payments/${id}/link`, payload);
    }
    // ── ออกใบกำกับภาษีฝากขายจาก "เงินที่เลือก" (spec 2026-08-13 รอบบ่าย) ──────────
    // RETIRED (spec 2026-11-03 §3B): BE ตอบ 409 use_sales_range_invoice — ใช้
    // consignmentRangeInvoiceCreate แทน; ไม่มีหน้าจอไหนเรียกแล้ว
    // body: { company_id, payment_ids: [..] } — เงินทุกใบต้องเป็นของบริษัทเดียวกัน
    // เป็นเงินฝากขาย (sale_order_id NULL) และยังไม่ผูกใบ (invoice_id NULL)
    // → { invoice: {...} } ยอดสุทธิ = ผลรวมเงินที่เลือกพอดี, เงินถูก stamp invoice_id ให้
    // ผิดพลาด (ตัวเลขประกอบอยู่ใน Datum ของ go-core — r.Set ไม่ใช่ r.Append):
    //   400 insufficient_sales → Datum { max_amount (รวม VAT), max_sub_total,
    //       requested_amount, requested_sub_total }
    //   409 payment_not_eligible → Datum { payment_ids, problems }
    //       (เงินผูกใบแล้ว / คนละบริษัท / เป็นเงินของ SO ขายขาด / ไม่มีแถวนั้น)
    consignmentInvoiceCreate(payload) {
        return this.request(HttpMethod.Post, "/api/v3/modern-trade/consignment-invoices", payload);
    }

    // ════════════════════════════════════════════════════════════════════════════
    // spec 2026-11-03 §3 — ออกใบกำกับก่อนรับเงิน
    // ════════════════════════════════════════════════════════════════════════════

    // §3A ขายขาด: 1 ใบกำกับต่อ 1 ใบส่งของ (source 1, status 1). BE ผูกเงินของ SO
    // ที่ยังไม่ผูกใบเข้าใบใหม่ให้อัตโนมัติ (เก่าก่อน ไม่เกินยอดใบ แตกแถวสุดท้ายถ้าเกิน)
    // → Data[0] { invoice_id, doc_no, net_amount, linked_amount, paid_amount, status }
    // 409 delivery_already_invoiced / has_source3_invoice / invoice_exists /
    // delivery_cancelled / sale_order_closed / state_changed · 400 not_outright /
    // nothing_to_bill / discount_exceeds_subtotal
    saleOrderInvoiceFromDelivery(saleOrderId, deliveryId) {
        return this.request(HttpMethod.Post, `/api/v3/modern-trade/sale-orders/${saleOrderId}/invoices/from-delivery`, {
            delivery_id: deliveryId,
        });
    }
    // §3B ฝากขาย: ตัวอย่างยอดขายที่ยังไม่ออกใบตามช่วงวันที่ขาย (รวมทั้งสองวัน)
    // q: company_id, from (YYYY-MM-DD), to (YYYY-MM-DD)
    // → Data[0] { lines:[{ product_id, wms_product_id, barcode, name, code, qty, unit_price, amount }],
    //     sub_total, vat, net, unpriced:[{ wms_product_id, barcode, name, code, qty }] }
    consignmentRangePreview(params) {
        return this.request(HttpMethod.Get, "/api/v3/modern-trade/consignment-invoices/preview", params);
    }
    // body: { company_id, from, to, expected_net } → Data[0] { invoice_id, doc_no, net }
    // (source 2, status 1). 409 nothing_to_bill / preview_stale (net moved since
    // the preview) · 400 unpriced_sales
    consignmentRangeInvoiceCreate(payload) {
        return this.request(HttpMethod.Post, "/api/v3/modern-trade/consignment-invoices/range", payload);
    }
}

export default ModernTradeProvider;
